/**
 * `skaile plugin` — manage registry plugins (drivers / connectors / deploy
 * targets) declared via `plugins:` in `skaile.yaml`.
 *
 * Not to be confused with `skaile integration` (the coding-agent adapter,
 * formerly `skaile plugin`). This group edits the `plugins:` list, reconciles
 * the project-local plugin store, and lists what each declared plugin
 * contributes to `pluginRegistry`.
 *
 *   skaile plugin install <spec>  [--project-dir <p>]
 *   skaile plugin remove  <name>  [--project-dir <p>]
 *   skaile plugin list            [--project-dir <p>]
 */

import path from "node:path";
import { Command } from "commander";
import { logErr, logInfo, logOk, logWarn, S } from "../helpers.ts";

interface ProjectOpts {
  projectDir: string;
}

/** Wrap an async action so a thrown error prints via logErr + sets a non-zero exit. */
function action(fn: (...args: never[]) => Promise<void>) {
  return async (...args: unknown[]): Promise<void> => {
    try {
      await (fn as (...a: unknown[]) => Promise<void>)(...args);
    } catch (err) {
      logErr(err instanceof Error ? err.message : String(err));
      process.exitCode = 1;
    }
  };
}

/** Reconcile + report the project-local plugin store for the given plugin list. */
async function reconcileAndReport(projectDir: string, plugins: string[]): Promise<void> {
  const { reconcilePlugins } = await import("../plugin-store/index.ts");
  if (plugins.length === 0) {
    logInfo("No plugins declared — nothing to reconcile.");
    return;
  }
  try {
    const result = await reconcilePlugins(projectDir, plugins);
    if (result.changed) logOk(`Reconciled plugin store (${result.reason}).`);
    else logInfo(`Plugin store ${result.reason}.`);
  } catch (err) {
    // A reconcile failure (e.g. package not published yet) is surfaced but does
    // not undo the skaile.yaml edit — the spec is recorded, install can retry.
    logWarn(`reconcile skipped: ${err instanceof Error ? err.message : String(err)}`);
  }
}

async function runInstall(spec: string, opts: ProjectOpts): Promise<void> {
  const projectDir = path.resolve(opts.projectDir);
  const { WorkspaceYamlEditor } = await import("@skaile/workspaces/core");
  const { specName } = await import("../plugin-store/index.ts");

  const yamlPath = path.join(projectDir, "skaile.yaml");
  const editor = WorkspaceYamlEditor.load(yamlPath);
  const replaced = editor.addPlugin(spec, specName);
  editor.save();

  if (replaced) logOk(`Updated ${S.cmd(specName(spec))} → ${S.cmd(spec)} in skaile.yaml`);
  else logOk(`Added ${S.cmd(spec)} to skaile.yaml plugins`);

  await reconcileAndReport(projectDir, editor.getPlugins());
}

async function runRemove(name: string, opts: ProjectOpts): Promise<void> {
  const projectDir = path.resolve(opts.projectDir);
  const { WorkspaceYamlEditor } = await import("@skaile/workspaces/core");
  const { specName } = await import("../plugin-store/index.ts");

  const yamlPath = path.join(projectDir, "skaile.yaml");
  const editor = WorkspaceYamlEditor.load(yamlPath);
  const removed = editor.removePlugin(name, specName);
  if (!removed) {
    logInfo(`No plugin matching ${S.cmd(name)} in skaile.yaml plugins.`);
    return;
  }
  editor.save();
  logOk(`Removed ${S.cmd(name)} from skaile.yaml plugins`);

  await reconcileAndReport(projectDir, editor.getPlugins());
}

async function runList(opts: ProjectOpts): Promise<void> {
  const projectDir = path.resolve(opts.projectDir);
  const { resolveSkWorkspaceConfig } = await import("@skaile/workspaces/core");
  const config = resolveSkWorkspaceConfig(projectDir);
  const declared = config.plugins ?? [];

  console.log();
  console.log(S.heading("  Declared plugins (skaile.yaml):"));
  if (declared.length === 0) {
    console.log(`  ${S.dim("(none)")}`);
  } else {
    for (const spec of declared) console.log(`  ${S.dim("·")} ${S.cmd(spec)}`);
  }

  // Load the declared plugins into a fresh registry so `list` reflects exactly
  // what this project's plugins contribute — independent of any built-ins the
  // process already registered. A load failure (store not materialized yet) is
  // non-fatal: the declared specs above are still printed.
  const { createPluginRegistry } = await import("@skaile/workspaces/plugin-registry");
  const registry = createPluginRegistry();
  if (declared.length > 0) {
    try {
      const { loadPlugins } = await import("../plugin-store/index.ts");
      const result = await loadPlugins(projectDir, declared, registry);
      for (const f of result.failed) logWarn(f.error);
    } catch (err) {
      logWarn(`could not load plugins: ${err instanceof Error ? err.message : String(err)}`);
    }
  }

  const kinds = [
    { kind: "driver" as const, label: "drivers" },
    { kind: "connector" as const, label: "connectors" },
    { kind: "deployTarget" as const, label: "deploy targets" },
  ];
  console.log();
  console.log(S.heading("  Contributed targets:"));
  let any = false;
  for (const { kind, label } of kinds) {
    const metas = registry.list(kind);
    if (metas.length === 0) continue;
    any = true;
    console.log(`  ${S.label(label)}: ${metas.map((m) => S.cmd(m.id)).join(", ")}`);
  }
  if (!any) console.log(`  ${S.dim("(none — plugins not installed, or contribute nothing)")}`);
  console.log();
}

/**
 * Creates the `skaile plugin` command group (registry plugins).
 *
 * @returns Configured {@link Command} ready for `program.addCommand()`.
 * @docLink cli/commands/plugin#make-plugin-command
 */
export function makePluginCommand(): Command {
  const cmd = new Command("plugin").description(
    "Manage registry plugins (drivers / connectors / deploy targets) declared in skaile.yaml",
  );

  cmd
    .command("install <spec>")
    .description("Add a plugin spec to skaile.yaml plugins and reconcile the store")
    .option("--project-dir <path>", "Project directory", process.cwd())
    .action(action(runInstall as never));

  cmd
    .command("remove <name>")
    .description("Remove a plugin (by package name) from skaile.yaml plugins and reconcile")
    .option("--project-dir <path>", "Project directory", process.cwd())
    .action(action(runRemove as never));

  cmd
    .command("list")
    .description("List declared plugins and the targets they contribute")
    .option("--project-dir <path>", "Project directory", process.cwd())
    .action(action(runList as never));

  cmd.action(() => cmd.help());
  return cmd;
}
